/**
 * MoneyInput — денежный ввод (рубли → копейки). Парсинг/форматирование строго через money.ts.
 * Коммит по blur/Enter, отмена по Escape. Показывает ошибку парсинга (aria-invalid).
 *
 * initialMinor — исходная сумма в копейках (строка). onCommit отдаёт нормализованные копейки.
 */

import { useRef, useState, type CSSProperties } from 'react';
import { minorToRubString, rubToMinor } from '../../lib/money';
import { cx } from './ui/cx';

export interface MoneyInputProps {
  initialMinor: string;
  onCommit: (minor: string) => void;
  onCancel?: () => void;
  autoFocus?: boolean;
  /** Разрешить пустое/нулевое значение. По умолчанию требуется > 0. */
  allowZero?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
  style?: CSSProperties;
}

/** Копейки → редактируемый текст рублей ("12345.60" → "12345.6", целые — без дробной части). */
function minorToEditable(minor: string): string {
  const s = minorToRubString(minor); // "12345.60"
  return s.replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
}

export function MoneyInput({
  initialMinor,
  onCommit,
  onCancel,
  autoFocus,
  allowZero = false,
  placeholder,
  ariaLabel,
  className,
  style,
}: MoneyInputProps): JSX.Element {
  const [text, setText] = useState<string>(() => minorToEditable(initialMinor));
  const [error, setError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const committedRef = useRef(false);

  const commit = (): void => {
    if (committedRef.current) return;
    try {
      const minor = rubToMinor(text);
      if (!allowZero && BigInt(minor) <= 0n) {
        setError(true);
        return;
      }
      committedRef.current = true;
      onCommit(minor);
    } catch {
      setError(true);
    }
  };

  return (
    <span className={cx('pw-money', error && 'pw-money--error', className)} style={style}>
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        className="pw-money__input pw-num"
        value={text}
        autoFocus={autoFocus}
        placeholder={placeholder}
        aria-label={ariaLabel ?? 'Сумма в рублях'}
        aria-invalid={error}
        onChange={(e) => {
          const v = e.target.value;
          if (/^[\d\s .,₽]*$/.test(v)) {
            setText(v);
            setError(false);
          }
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            committedRef.current = true;
            onCancel?.();
          }
        }}
      />
      <span className="pw-money__suffix" aria-hidden>
        ₽
      </span>
    </span>
  );
}
